import api from '../api';

export interface Risk {
  id: string;
  riskId: string;
  name: string;
  description: string;
  category: string;
  riskLevel: 'HIGH' | 'MEDIUM' | 'LOW' | 'CRITICAL';
  taxonomy: string[];
  aiGenerated?: boolean;
  confidence?: number;
}

export interface Control {
  id: string;
  code: string;
  name: string;
  description: string;
  type: string;
  frequency: string;
  owner: string;
  lastTested: string;
  effectiveness?: 'Effective' | 'Needs Review' | 'Ineffective';
  regulatory: {
    framework: string;
    reference: string;
    description: string;
  }[];
  aiGenerated?: boolean;
  confidence?: number;
}

export interface SaveRisksRequest {
  risks: Array<{
    name: string;
    description: string;
    category?: string;
    riskLevel?: string;
    taxonomy?: string[];
    confidence?: number;
    reasoning?: string;
  }>;
}

export interface SaveControlsRequest {
  controls: Array<{
    id?: string;
    name: string;
    description: string;
    type: string;
    frequency: string;
    owner: string;
    effectiveness?: string;
    regulatory?: any[];
    confidence?: number;
    reasoning?: string;
  }>;
}

class ProcessStepRiskControlService {
  /**
   * Save AI-suggested risks to the database
   */
  async saveRisks(processStepId: number, risks: SaveRisksRequest['risks']): Promise<Risk[]> {
    try {
      const response = await api.post(
        `/api/process-steps/${processStepId}/risks`,
        { risks }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save risks');
      }

      return response.data.risks;
    } catch (error: any) {
      console.error('Error saving risks:', error);
      throw new Error(error.response?.data?.message || 'Failed to save risks to database');
    }
  }

  /**
   * Save AI-suggested controls to the database
   */
  async saveControls(processStepId: number, controls: SaveControlsRequest['controls']): Promise<Control[]> {
    try {
      const response = await api.post(
        `/api/process-steps/${processStepId}/controls`,
        { controls }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save controls');
      }

      return response.data.controls;
    } catch (error: any) {
      console.error('Error saving controls:', error);
      throw new Error(error.response?.data?.message || 'Failed to save controls to database');
    }
  }

  /**
   * Get all risks and controls for a process step from database
   */
  async getRisksAndControls(processStepId: number): Promise<{ risks: Risk[], controls: Control[] }> {
    try {
      const response = await api.get(
        `/api/process-steps/${processStepId}/risks-controls`
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch risks and controls');
      }

      return {
        risks: response.data.risks || [],
        controls: response.data.controls || []
      };
    } catch (error: any) {
      console.error('Error fetching risks and controls:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch risks and controls from database');
    }
  }

  /**
   * Delete a risk assessment
   */
  async deleteRisk(processStepId: number, riskAssessmentId: string): Promise<void> {
    try {
      const response = await api.delete(
        `/api/process-steps/${processStepId}/risks/${riskAssessmentId}`
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete risk');
      }
    } catch (error: any) {
      console.error('Error deleting risk:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete risk from database');
    }
  }

  /**
   * Delete a control
   */
  async deleteControl(processStepId: number, controlId: string): Promise<void> {
    try {
      const response = await api.delete(
        `/api/process-steps/${processStepId}/controls/${controlId}`
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete control');
      }
    } catch (error: any) {
      console.error('Error deleting control:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete control from database');
    }
  }

  /**
   * Get comprehensive risk & control mapping for a project
   */
  async getComprehensiveMapping(projectId: number): Promise<any> {
    try {
      const response = await api.get(
        `/api/risk-control-mapping/project/${projectId}/comprehensive`
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch comprehensive mapping');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error fetching comprehensive mapping:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch comprehensive mapping from database');
    }
  }
}

export default new ProcessStepRiskControlService();